import { DeleteUndo } from '../components/DeleteUndo';
import { useDeleteUndo } from '../components/useDeleteUndo';
import type { MailboxStatus } from '../../domain/mailbox-status';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SessionUser } from '../../shared/contracts';
import { HISTORY_LOOKBACK_DAYS, HISTORY_MESSAGES_PER_DAY, type HistoryLookbackDays } from '../../domain/history-lookback';
import { pickCompletionMessage } from '../../domain/encouragement';
import { api, ApiError } from '../api';
import { EventCard } from '../components/EventCard';
import { EditDialog, IgnoreSourceDialog } from '../components/EventDialogs';
import { PandaMark } from '../components/PandaMark';
import type { EventRecord } from '../../shared/contracts';
import type { DashboardResponse, ProcessResponse } from '../types';

function timeLabel(value: string | null): string {
  if (!value) return '初回確認待ち';
  return new Intl.DateTimeFormat('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' }).format(new Date(value));
}

export function DashboardPage({ user }: { user: SessionUser }) {
  const [status, setStatus] = useState<MailboxStatus | null>(null);
  const [statusError, setStatusError] = useState('');
  const mailboxBusyRef = useRef(false);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [operationError, setOperationError] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [historyDays, setHistoryDays] = useState<HistoryLookbackDays>(3);
  const [historyProcessing, setHistoryProcessing] = useState(false);
  const [historyNotice, setHistoryNotice] = useState('');
  const [notice, setNotice] = useState('');
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [archiving, setArchiving] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EventRecord | null>(null);
  const [ignoring, setIgnoring] = useState<EventRecord | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const confirmingAllRef = useRef(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  const deletingRef = useRef(new Set<string>());
  const archivingRef = useRef(new Set<string>());
  const refreshTimerRef = useRef<number | null>(null);
  const loadRequestRef = useRef(0);
  const loadAbortRef = useRef<AbortController | null>(null);
  const statusRequestRef = useRef(0);
  const forcedReconnectRef = useRef(false);
  const loadStatus = async (signal?: AbortSignal) => {
    const requestId = ++statusRequestRef.current;
    try {
      const result = await api<{ status: MailboxStatus }>('/api/mailbox/status', { signal });
      if (requestId === statusRequestRef.current) { setStatus(forcedReconnectRef.current ? { ...result.status, state: 'reconnect_required' } : result.status); setStatusError(''); }
    } catch { if (requestId === statusRequestRef.current && !signal?.aborted) { setStatus(null); setStatusError('監視状態を確認できません。再読み込みしてください'); } }
  };
  const completionMessage = useRef(pickCompletionMessage()).current;
  const load = async () => {
    const requestId = ++loadRequestRef.current;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    void loadStatus(controller.signal);
    try {
      const result = await api<DashboardResponse>('/api/dashboard', { signal: controller.signal });
      if (requestId === loadRequestRef.current) { setData(result); setError(''); }
    } catch (reason) {
      if (requestId === loadRequestRef.current && !controller.signal.aborted) {
        if (reason instanceof ApiError && ['GOOGLE_REAUTH_REQUIRED', 'GOOGLE_NOT_CONNECTED'].includes(reason.code)) {
          forcedReconnectRef.current = true;
          setStatus((current) => ({ ...(current ?? { lastCheckAt: null, lastSuccessAt: null, failed: 0, retryWaiting: 0, retryStopped: 0 }), state: 'reconnect_required' }));
        }
        setError(reason instanceof Error ? reason.message : '予定を読み込めませんでした');
      }
    }
  };
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void loadStatus(); }, 60_000);
    const onVisible = () => { if (document.visibilityState === 'visible') void loadStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); statusRequestRef.current += 1; };
  }, []);
  useEffect(() => () => {
    loadRequestRef.current += 1; loadAbortRef.current?.abort();
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
  }, []);
  const undo = useDeleteUndo(async (restored) => { if (restored) setNotice(`${restored}件の予定を元に戻しました。`); else setNotice(''); await load(); });
  const scheduleRefresh = () => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => void load(), 350);
  };
  const processNow = async () => {
    if (confirmingAllRef.current || mailboxBusyRef.current) return; mailboxBusyRef.current = true;
    setProcessing(true); setOperationError('');
    try { await api('/api/process-now', { method: 'POST' }); await load(); }
    catch (reason) { setOperationError(reason instanceof Error ? reason.message : '確認できませんでした'); }
    finally { mailboxBusyRef.current = false; setProcessing(false); await load(); }
  };
  const processHistory = async () => {
    if (confirmingAllRef.current || mailboxBusyRef.current) return; mailboxBusyRef.current = true;
    setHistoryProcessing(true); setError(''); setHistoryNotice('');
    try {
      const summary: ProcessResponse['summary'] = { emails: 0, created: 0, updated: 0, skipped: 0, errors: 0, truncated: false, remaining: false };
      for (let dayOffset = 0; dayOffset < historyDays; dayOffset += 1) {
        const result = await api<ProcessResponse>('/api/process-history', { method: 'POST', body: JSON.stringify({ dayOffset }) });
        summary.emails += result.summary.emails;
        summary.created += result.summary.created;
        summary.updated += result.summary.updated;
        summary.skipped += result.summary.skipped;
        summary.errors += result.summary.errors;
        summary.truncated ||= result.summary.truncated;
        summary.remaining ||= result.summary.remaining;
      }
      const errors = summary.errors ? `、エラー${summary.errors}件` : '';
      const limit = summary.truncated ? ` 1日${HISTORY_MESSAGES_PER_DAY}通の確認上限に達した日があります。` : '';
      const remaining = summary.remaining ? ' 続きは次の確認で安全に処理します。' : '';
      setHistoryNotice(`${historyDays}日分のメール${summary.emails}通を確認しました。新しい予定・候補${summary.created}件、更新${summary.updated}件${errors}。${limit}${remaining}`);
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : '過去のメールを確認できませんでした'); }
    finally { mailboxBusyRef.current = false; setHistoryProcessing(false); }
  };
  const remove = async (id: string) => {
    if (confirmingAllRef.current || deletingRef.current.has(id)) return;
    deletingRef.current.add(id);
    setDeleting((current) => new Set([...current, id])); setError(''); setNotice('');
    try {
      const result = await api<{ deleted: number; successIds: string[] }>('/api/events/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: [id] }) });
      if (result.deleted !== 1) throw new Error('予定を削除できませんでした。画面を再読み込みして、もう一度お試しください。');
      setData((current) => current ? { ...current, pending: Math.max(0, current.pending - 1), events: current.events.filter((event) => event.id !== id) } : current);
      undo.add(result.successIds, data?.events.find((event) => event.id === id)?.title ?? '予定');
      setNotice('予定を削除しました。');
    } catch (reason) {
      setOperationError(reason instanceof Error ? reason.message : '予定を削除できませんでした');
    } finally {
      deletingRef.current.delete(id);
      setDeleting((current) => { const next = new Set(current); next.delete(id); return next; });
      scheduleRefresh();
    }
  };
  const archive = async (id: string) => {
    if (confirmingAllRef.current || archivingRef.current.has(id)) return;
    archivingRef.current.add(id);
    setArchiving((current) => new Set([...current, id])); setError(''); setNotice('');
    try {
      const result = await api<{ archived: number }>('/api/events/bulk-archive', { method: 'POST', body: JSON.stringify({ ids: [id], category: 'confirmed' }) });
      if (result.archived !== 1) throw new Error('予定を確認して残す処理ができませんでした。画面を再読み込みして、もう一度お試しください。');
      setData((current) => current ? { ...current, pending: Math.max(0, current.pending - 1), events: current.events.filter((event) => event.id !== id) } : current);
      setNotice('確認して残しました。Googleカレンダーには残っています。');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '予定を確認して残す処理ができませんでした');
    } finally {
      archivingRef.current.delete(id);
      setArchiving((current) => { const next = new Set(current); next.delete(id); return next; });
      scheduleRefresh();
    }
  };
  const confirmAll = async () => {
    if (mailboxBusyRef.current || confirmingAllRef.current || deletingRef.current.size || archivingRef.current.size || editing || ignoring) return;
    confirmingAllRef.current = true; setConfirmingAll(true); setError(''); setOperationError(''); setNotice('');
    // Invalidate an older read so it cannot restore cards after the write.
    loadRequestRef.current += 1; loadAbortRef.current?.abort();
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    try {
      const result = await api<{ confirmed: number }>('/api/events/confirm-all', { method: 'POST' });
      setData((current) => current ? { ...current, pending: 0, events: [] } : current);
      setNotice(result.confirmed + '件すべて残しました。Googleカレンダーと「残した予定」で確認できます。');
      await load();
    } catch (reason) {
      setOperationError(reason instanceof Error ? reason.message : '全部残す処理ができませんでした');
      await load();
    } finally { confirmingAllRef.current = false; setConfirmingAll(false); }
  };
  const ignoreSource = async (pattern: string, field: 'subject' | 'email') => {
    if (!ignoring) return;
    setDialogBusy(true); setError(''); setNotice('');
    try {
      const result = await api<{ deleted: number; pattern: string }>(`/api/events/${ignoring.id}/ignore-source`, { method: 'POST', body: JSON.stringify({ pattern, field }) });
      setIgnoring(null);
      setNotice(result.deleted > 0
        ? `「${result.pattern}」を今後拾わない設定にし、今回の予定${result.deleted}件を削除しました。`
        : `「${result.pattern}」を今後拾わない設定にしました。`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '今後拾わない設定にできませんでした');
    } finally {
      setDialogBusy(false);
    }
  };
  return (
    <div className="page dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-intro">
          <PandaMark size={92} animated />
          <div className="dashboard-hero-copy">
            <p className="eyebrow">{user.displayName}さん</p>
            <h1>見つけた予定</h1>
            <p className="dashboard-status"><span className={`status-dot ${status?.state === 'normal' ? 'ok' : ''}`} /><Link to="/settings#mail-status">{status ? ({ normal: '自動チェック中', waiting: '初回チェック待ち', delayed: '自動チェックを待っています', reconnect_required: 'Googleの再接続が必要' })[status.state] : statusError ? '状態を確認できません' : '状態を確認中…'}</Link>{status?.lastCheckAt && <small>{timeLabel(status.lastCheckAt)} 更新</small>}</p>
          </div>
        </div>
        <button className="button primary" onClick={processNow} disabled={confirmingAll || processing || historyProcessing || status?.state === 'reconnect_required'}>{processing ? '確認中…' : '今すぐ確認'}</button>
      </section>
      <Link to="/quick-add" className="quick-add-entry">
        <span className="quick-add-entry-icon" aria-hidden="true">＋</span>
        <span><strong>予定を追加</strong><small>話す・文章を入力する</small></span>
        <span className="quick-add-entry-arrow" aria-hidden="true">→</span>
      </Link>
      {status?.state === 'reconnect_required' && <div className="alert warning"><a className="button primary" href="/api/auth/google/start">Googleを再接続</a></div>}
      <DeleteUndo undo={undo} />
      {(operationError || error) && <div className="alert error">{operationError || error}</div>}
      {notice && <div className="alert success" role="status">{notice}</div>}
      {data?.calendarSync.state === 'permission_required' && <div className="alert warning calendar-sync-alert"><p>Googleカレンダーで削除・移動した内容もパンダへ戻すには、カレンダーの読み取り許可が必要です。</p><a className="button primary" href="/api/auth/google/start">Googleを再接続</a></div>}
      {data?.calendarSync.state === 'error' && <div className="alert warning">カレンダー側の変更確認を一時的に行えませんでした。次の確認で再試行します。</div>}
      <Link to="/events" className="pending-summary" aria-label={data ? `${data.pending}件の予定を確認する` : error ? '予定を読み込めませんでした' : '予定を読み込み中'}>{data ? <><span>確認する予定</span><strong>{data.pending}件</strong><span className="pending-link">すべて見る →</span></> : <span>{error ? '予定を読み込めませんでした' : '予定を読み込み中…'}</span>}</Link>
      {Boolean(data?.pending) && <div className="confirm-all-panel"><button className="button primary" onClick={confirmAll} disabled={confirmingAll || deleting.size > 0 || archiving.size > 0 || processing || historyProcessing || status?.state === 'reconnect_required'}>{confirmingAll ? '残しています…' : '全部残す（' + data!.pending + '件）'}</button><small>確認待ちをすべて「残した予定」へ。カレンダーにも残ります。</small></div>}
      <div className="event-list compact">
        {data?.events.map((event) => <EventCard key={event.id} event={event} selectable={false} selected={false} deleting={deleting.has(event.id)} archiving={confirmingAll || archiving.has(event.id)} onSelect={() => undefined} onDelete={remove} onArchive={archive} onEdit={(event) => { if (!confirmingAllRef.current) setEditing(event); }} onIgnoreSource={(event) => { if (!confirmingAllRef.current) setIgnoring(event); }} />)}
        {data && data.events.length === 0 && <div className="empty-state completion"><span>✓</span><h3>確認完了！</h3><p>{completionMessage}</p></div>}
        {!data && !error && <div className="skeleton-list"><i /><i /><i /></div>}
      </div>
      <details className="quiet-details dashboard-tools">
        <summary>その他の操作</summary>
        <section className="history-check" aria-labelledby="history-check-title">
        <div><strong id="history-check-title">過去のメールを確認</strong><small>1日最大{HISTORY_MESSAGES_PER_DAY}通。</small></div>
        <div className="history-check-actions">
          <label><span className="sr-only">さかのぼる期間</span><select value={historyDays} onChange={(event) => setHistoryDays(Number(event.target.value) as HistoryLookbackDays)} disabled={confirmingAll || historyProcessing || processing || status?.state === 'reconnect_required'}>{HISTORY_LOOKBACK_DAYS.map((days) => <option key={days} value={days}>{days}日</option>)}</select></label>
          <button className="button secondary" onClick={processHistory} disabled={confirmingAll || historyProcessing || processing || status?.state === 'reconnect_required'}>{historyProcessing ? '確認中…' : '確認する'}</button>
        </div>
      </section>
      </details>
      {historyNotice && <div className="alert success" role="status">{historyNotice}</div>}
      {editing && <EditDialog event={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); setNotice('予定を変更しました。'); await load(); }} />}
      {ignoring && <IgnoreSourceDialog event={ignoring} busy={dialogBusy} onClose={() => !dialogBusy && setIgnoring(null)} onConfirm={ignoreSource} />}
    </div>
  );
}
